"use client";

import { useRef, useEffect, useCallback } from "react";

export default function CustomCursor() {
  const ringRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);
  const mouse = useRef({ x: -100, y: -100 });
  const ring = useRef({ x: -100, y: -100 });
  const dot = useRef({ x: -100, y: -100 });
  const hover = useRef(false);
  const visible = useRef(false);
  const raf = useRef(0);

  const isInteractive = useCallback((target: HTMLElement | null) => {
    if (!target) return false;
    return (
      target.tagName === "A" ||
      target.tagName === "BUTTON" ||
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      !!target.closest("a") ||
      !!target.closest("button") ||
      !!target.closest("[data-cursor='pointer']")
    );
  }, []);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (window.matchMedia("(pointer: coarse)").matches) return;

    document.body.classList.add("premium-cursor");

    const onMove = (e: MouseEvent) => {
      mouse.current = { x: e.clientX, y: e.clientY };
      if (!visible.current) {
        visible.current = true;
        ring.current = { x: e.clientX, y: e.clientY };
        dot.current = { x: e.clientX, y: e.clientY };
        if (ringRef.current) ringRef.current.style.opacity = "1";
        if (logoRef.current) logoRef.current.style.opacity = "1";
      }
    };

    const onOver = (e: MouseEvent) => {
      hover.current = isInteractive(e.target as HTMLElement);
    };

    const onLeave = () => {
      visible.current = false;
      if (ringRef.current) ringRef.current.style.opacity = "0";
      if (logoRef.current) logoRef.current.style.opacity = "0";
    };

    const loop = () => {
      const h = hover.current;
      // Ring eases smoothly, dot follows faster — premium interpolation
      ring.current.x += (mouse.current.x - ring.current.x) * 0.16;
      ring.current.y += (mouse.current.y - ring.current.y) * 0.16;
      dot.current.x += (mouse.current.x - dot.current.x) * 0.55;
      dot.current.y += (mouse.current.y - dot.current.y) * 0.55;

      if (ringRef.current) {
        const size = h ? 56 : 36;
        ringRef.current.style.width = `${size}px`;
        ringRef.current.style.height = `${size}px`;
        ringRef.current.style.background = h
          ? "rgba(201,168,76,0.08)"
          : "rgba(201,168,76,0.02)";
        ringRef.current.style.borderColor = h
          ? "rgba(223,192,106,0.7)"
          : "rgba(201,168,76,0.45)";
        ringRef.current.style.boxShadow = h
          ? "0 0 26px rgba(201,168,76,0.25), inset 0 0 12px rgba(201,168,76,0.12)"
          : "0 0 14px rgba(201,168,76,0.12)";
        ringRef.current.style.transform = `translate3d(${ring.current.x}px, ${ring.current.y}px, 0) translate(-50%, -50%)`;
      }

      if (logoRef.current) {
        const lSize = h ? 30 : 22;
        logoRef.current.style.width = `${lSize}px`;
        logoRef.current.style.height = `${lSize}px`;
        logoRef.current.style.filter = h
          ? "drop-shadow(0 1px 6px rgba(201,168,76,0.55))"
          : "drop-shadow(0 1px 4px rgba(201,168,76,0.35))";
        logoRef.current.style.transform = `translate3d(${dot.current.x}px, ${dot.current.y}px, 0) translate(-50%, -50%)`;
      }

      raf.current = requestAnimationFrame(loop);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseover", onOver, { passive: true });
    document.documentElement.addEventListener("mouseleave", onLeave);

    raf.current = requestAnimationFrame(loop);

    return () => {
      document.body.classList.remove("premium-cursor");
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseover", onOver);
      document.documentElement.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(raf.current);
    };
  }, [isInteractive]);

  return (
    <>
      <div
        ref={ringRef}
        className="fixed top-0 left-0 z-[9999] pointer-events-none rounded-full border opacity-0 transition-[opacity,border-color,background,box-shadow] duration-300 hidden md:block"
        style={{
          width: 36,
          height: 36,
          borderWidth: 1,
          borderColor: "rgba(201,168,76,0.45)",
          background: "rgba(201,168,76,0.02)",
          boxShadow: "0 0 14px rgba(201,168,76,0.12)",
          willChange: "transform, width, height",
        }}
      />
      {/* eslint-disable-next-line @next/next/no-img-element -- 22px overlay cursor; next/image not suitable for a pointer overlay */}
      <img
        ref={logoRef}
        src="/images/logo-main.png"
        alt=""
        draggable={false}
        className="fixed top-0 left-0 z-[9999] pointer-events-none opacity-0 hidden md:block select-none"
        style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          objectFit: "cover",
          willChange: "transform, width, height",
          transition: "opacity 0.3s, width 0.25s ease, height 0.25s ease",
        }}
      />
    </>
  );
}